import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Form, FormGroup, Label, Input, Row, Col, Card, Button } from "reactstrap";
import swal from "sweetalert";
import http from "../../../../utils/http-common";
import { getPublicUrl } from "../../../../utils/Utils";

/** Avoid double swal: http-common interceptor also alerts on `response.data.error`. */
const SUPPRESS_INLINE_SWAL = { suppressInlineErrorSwal: true };

function apiErrorMessage(err, fallback) {
  const d = err?.response?.data;
  if (typeof d?.error === "string" && d.error.trim()) return d.error.trim();
  if (typeof d?.message === "string" && d.message.trim()) return d.message.trim();
  if (err?.message && String(err.message).trim()) return String(err.message).trim();
  return fallback;
}

/** Extra guidance when Google OAuth returns invalid_grant (refresh token rejected). */
function sendTestFailureHint(fullMessage) {
  if (!fullMessage || !/invalid_grant/i.test(fullMessage)) return fullMessage;
  return `${fullMessage}\n\nWhat to do: Generate a new refresh token with the same Client ID and Client Secret as saved on this screen (e.g. Google OAuth Playground — set your OAuth client credentials in the playground settings before “Exchange authorization code”). Revoked, expired, or tokens created for another client cause this error. Enable Gmail API on the project and use a send scope. Set “OAuth mailbox” to the Google account that clicked Allow.`;
}

const GMAIL_OAUTH_FIELDS = [
  {
    key: "gmail_email_from",
    label: "OAuth mailbox (Google account)",
    help: "Must match the account used to generate the refresh token.",
    sensitive: false,
    inputType: "email",
    placeholder: "noreply@yourdomain.com",
  },
  {
    key: "gmail_client_id",
    label: "OAuth Client ID",
    help: "Google Cloud Console → APIs & Services → Credentials.",
    sensitive: true,
    inputType: "password",
    placeholder: "xxx.apps.googleusercontent.com",
  },
  {
    key: "gmail_client_secret",
    label: "OAuth Client Secret",
    help: "Paste a new value only when rotating; saved values stay until replaced.",
    sensitive: true,
    inputType: "password",
    placeholder: "GOCSPX-…",
  },
  {
    key: "gmail_refresh_token",
    label: "OAuth refresh token",
    help: "OAuth 2.0 Playground or your flow, with Gmail send scope.",
    sensitive: true,
    inputType: "password",
    placeholder: "1//0…",
  },
];

/**
 * Single admin UI for outbound email: Gmail OAuth, visible From, checklist, test send.
 * Mounted only from Settings → Email delivery.
 */
export default function EmailDeliverySettingsPanel() {
  const [config, setConfig] = useState({
    fromAddress: "",
    fromName: "",
    transportConfigured: false,
    transportNote: "",
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);
  const [configForm, setConfigForm] = useState({ fromAddress: "", fromName: "", email_provider: "" });
  const [emailDiagnostics, setEmailDiagnostics] = useState(null);
  const [oauthForm, setOauthForm] = useState({
    gmail_email_from: "",
    gmail_client_id: "",
    gmail_client_secret: "",
    gmail_refresh_token: "",
  });
  const [oauthSaving, setOauthSaving] = useState(false);
  const [showSecretInputs, setShowSecretInputs] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [testSending, setTestSending] = useState(false);

  const copyField = useCallback(async (label, text) => {
    if (!text || !String(text).trim()) return;
    try {
      await navigator.clipboard.writeText(String(text).trim());
      swal("Copied", `${label} copied to clipboard.`, "success");
    } catch {
      swal("Error", "Could not copy — select the text manually.", "error");
    }
  }, []);

  const fetchBundle = useCallback(async () => {
    try {
      setConfigLoading(true);
      const res = await http.get("/api/v1/email-delivery?revealSecrets=1", SUPPRESS_INLINE_SWAL);
      const bundle = res.data?.data;
      if (!bundle) throw new Error("Empty email-delivery response");
      setEmailDiagnostics(bundle.diagnostics || null);
      const ui = bundle.ui || {};
      const display = bundle.display || {};
      const fromAddress = display.email_from_address ?? ui.fromAddress ?? "";
      const fromName = display.email_from_name ?? ui.fromName ?? "";
      setConfig({
        fromAddress: ui.fromAddress || "",
        fromName: ui.fromName || "",
        transportConfigured: !!ui.transportConfigured,
        transportNote: ui.transportNote || "",
      });
      setConfigForm({
        fromAddress,
        fromName,
        email_provider: display.email_provider ?? "",
      });
      const fields = Array.isArray(bundle.oauth?.fields) ? bundle.oauth.fields : [];
      const nextOauth = {
        gmail_email_from: "",
        gmail_client_id: "",
        gmail_client_secret: "",
        gmail_refresh_token: "",
      };
      fields.forEach((f) => {
        if (!f || !f.key || !Object.prototype.hasOwnProperty.call(nextOauth, f.key)) return;
        nextOauth[f.key] =
          f.configured && f.masked != null && String(f.masked).trim() !== ""
            ? String(f.masked)
            : "";
      });
      setOauthForm(nextOauth);
    } catch (err) {
      swal("Error", apiErrorMessage(err, "Failed to load email delivery settings"), "error");
    } finally {
      setConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBundle();
  }, [fetchBundle]);

  const handleSaveDisplayFrom = async (e) => {
    e.preventDefault();
    try {
      setConfigSaving(true);
      await http.put(
        "/api/v1/email-delivery/display-from",
        {
          email_from_address: configForm.fromAddress,
          email_from_name: configForm.fromName,
          email_provider: configForm.email_provider || undefined,
        },
        SUPPRESS_INLINE_SWAL
      );
      swal("Success", "Sender name and address saved.", "success");
      fetchBundle();
    } catch (err) {
      swal("Error", apiErrorMessage(err, "Failed to save"), "error");
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSaveOAuth = async (e) => {
    e.preventDefault();
    const body = {};
    Object.keys(oauthForm).forEach((k) => {
      const v = oauthForm[k];
      if (v == null || String(v).trim() === "") return;
      body[k] = String(v).trim();
    });
    if (Object.keys(body).length === 0) {
      swal("Info", "No new values to save. Leave placeholders unchanged or paste new secrets.", "info");
      return;
    }
    try {
      setOauthSaving(true);
      await http.put("/api/v1/email-delivery/gmail-oauth", body, SUPPRESS_INLINE_SWAL);
      swal("Success", "Gmail OAuth saved.", "success");
      fetchBundle();
    } catch (err) {
      swal("Error", apiErrorMessage(err, "Failed to save OAuth settings"), "error");
    } finally {
      setOauthSaving(false);
    }
  };

  const handleSendTest = async (e) => {
    e.preventDefault();
    const to = String(testTo || "").trim();
    if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      swal("Error", "Enter a valid recipient email.", "error");
      return;
    }
    try {
      setTestSending(true);
      await http.post("/api/v1/email-delivery/send-test", { to }, SUPPRESS_INLINE_SWAL);
      swal("Success", "Test email sent.", "success");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("[email-delivery/send-test]", err.response?.status, err.response?.data);
      }
      swal("Error", sendTestFailureHint(apiErrorMessage(err, "Failed to send test email")), "error");
    } finally {
      setTestSending(false);
    }
  };

  if (configLoading) {
    return <p className="text-soft">Loading…</p>;
  }

  return (
    <div>
      <p className="text-soft small mb-4">
        This screen is the <strong>only</strong> place to set Gmail OAuth and the visible sender for outbound mail. Edit message content
        under{" "}
        <Link to={getPublicUrl("email-templates-management")}>Email templates</Link>. The email integration on/off label in{" "}
        <Link to={`${getPublicUrl("admin-settings")}?tab=integrations`}>Integrations</Link> does not block sending by itself.
      </p>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">Status</h6>
          <p className="small mb-2">
            <strong>Transport (Gmail OAuth):</strong>{" "}
            <span className={config.transportConfigured ? "text-success" : "text-warning"}>
              {config.transportConfigured ? "Ready" : "Not configured"}
            </span>
          </p>
          <p className="text-soft small mb-0">{config.transportNote}</p>
        </div>
      </Card>

      {emailDiagnostics?.checklist?.length > 0 && (
        <Card className="card-bordered mb-4">
          <div className="card-inner">
            <h6 className="card-title mb-3">Checklist</h6>
            <ul className="list-unstyled small mb-0">
              {emailDiagnostics.checklist.map((item) => (
                <li key={item.id} className="mb-3">
                  <span className={item.ok ? "text-success" : "text-warning"}>{item.ok ? "✓ " : "• "}</span>
                  <strong>{item.label}</strong>
                  <div className="text-soft mt-1">{item.detail}</div>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">Gmail OAuth</h6>
          <p className="text-soft small mb-3">Required for the server to send mail through Google.</p>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Button
              color="light"
              size="sm"
              type="button"
              onClick={() => setShowSecretInputs((v) => !v)}
            >
              {showSecretInputs ? "Mask secrets in form" : "Show secrets in form"}
            </Button>
            <span className="text-soft small">Secrets load from the server for this admin session only. Use Copy or select text to copy.</span>
          </div>
          <Form onSubmit={handleSaveOAuth}>
            {GMAIL_OAUTH_FIELDS.map((field) => (
              <FormGroup key={field.key}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <Label className="mb-0" htmlFor={`ed-oauth-${field.key}`}>
                    {field.label}
                  </Label>
                  {!!oauthForm[field.key] && (
                    <Button
                      color="light"
                      size="sm"
                      type="button"
                      onClick={() => copyField(field.label, oauthForm[field.key])}
                      disabled={oauthSaving}
                    >
                      Copy
                    </Button>
                  )}
                </div>
                <Input
                  id={`ed-oauth-${field.key}`}
                  type={field.sensitive && !showSecretInputs ? field.inputType : "text"}
                  autoComplete="off"
                  className={field.sensitive ? "font-monospace small" : ""}
                  placeholder={field.placeholder}
                  value={oauthForm[field.key] || ""}
                  onChange={(e) => setOauthForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  disabled={oauthSaving}
                />
                {field.help && <span className="form-note d-block mt-1">{field.help}</span>}
              </FormGroup>
            ))}
            <Button color="primary" type="submit" disabled={oauthSaving}>
              {oauthSaving ? "Saving…" : "Save Gmail OAuth"}
            </Button>
          </Form>
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">Sender (From)</h6>
          <p className="text-soft small mb-3">What recipients see in their inbox. Must align with your Google account or a verified alias.</p>
          <Form onSubmit={handleSaveDisplayFrom}>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label>From address</Label>
                  <Input
                    type="email"
                    value={configForm.fromAddress}
                    onChange={(e) => setConfigForm((p) => ({ ...p, fromAddress: e.target.value }))}
                    placeholder="e.g. noreply@inoqare.com"
                    disabled={configSaving}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>From name</Label>
                  <Input
                    type="text"
                    value={configForm.fromName}
                    onChange={(e) => setConfigForm((p) => ({ ...p, fromName: e.target.value }))}
                    placeholder="e.g. Inoqare"
                    disabled={configSaving}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Provider note (optional)</Label>
                  <Input
                    type="text"
                    value={configForm.email_provider}
                    onChange={(e) => setConfigForm((p) => ({ ...p, email_provider: e.target.value }))}
                    placeholder="e.g. Gmail"
                    disabled={configSaving}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Button color="primary" type="submit" disabled={configSaving}>
              {configSaving ? "Saving…" : "Save sender"}
            </Button>
          </Form>
        </div>
      </Card>

      <Card className="card-bordered mb-4">
        <div className="card-inner">
          <h6 className="card-title mb-3">Send test email</h6>
          <p className="text-soft small mb-3">Verify OAuth and sender settings end-to-end.</p>
          <Form onSubmit={handleSendTest}>
            <Row className="align-items-end">
              <Col md="8">
                <FormGroup className="mb-md-0">
                  <Label htmlFor="ed-test-to">Recipient</Label>
                  <Input
                    id="ed-test-to"
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="you@company.com"
                    disabled={testSending}
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <Button color="light" type="submit" disabled={testSending} block>
                  {testSending ? "Sending…" : "Send test"}
                </Button>
              </Col>
            </Row>
          </Form>
        </div>
      </Card>
    </div>
  );
}
